import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ChevronDownIcon from './icons/ChevronDownIcon';

const formatKey = (key: string) => {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
};

const SmileScoreDisplay: React.FC<{ ratingData: any, afterRatingData?: any | null }> = ({ ratingData, afterRatingData }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!ratingData) {
        return (
            <div className="bg-white/5 rounded-xl p-4 flex items-center gap-4 animate-pulse">
                <div className="w-20 h-20 rounded-full bg-zinc-700/50 flex-shrink-0" />
                <div className="flex-1">
                    <div className="h-4 bg-zinc-700/50 rounded w-1/2" />
                    <div className="h-3 bg-zinc-700/50 rounded w-3/4 mt-2" />
                </div>
            </div>
        );
    }

    const { overallSmileScore, ...analysisSections } = ratingData;
    const score = overallSmileScore?.score ?? 0;
    const summary = overallSmileScore?.summary ?? "Analysis not available.";
    const afterScore = afterRatingData?.overallSmileScore?.score;

    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    const scoreColor = score >= 80 ? 'text-teal-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400';

    return (
        <motion.div layout className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-4 flex items-center gap-4 text-left"
                aria-expanded={isExpanded}
            >
                <div className="relative w-20 h-20 flex-shrink-0">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                            className="text-zinc-700/50"
                            stroke="currentColor"
                            strokeWidth="10"
                            cx="50"
                            cy="50"
                            r={radius}
                            fill="transparent"
                        />
                        <motion.circle
                            className={scoreColor}
                            stroke="currentColor"
                            strokeWidth="10"
                            strokeLinecap="round"
                            cx="50"
                            cy="50"
                            r={radius}
                            fill="transparent"
                            transform="rotate(-90 50 50)"
                            strokeDasharray={circumference}
                            initial={{ strokeDashoffset: circumference }}
                            animate={{ strokeDashoffset }}
                            transition={{ duration: 1.5, ease: 'easeOut' }}
                        />
                    </svg>
                    <div className={`absolute inset-0 flex flex-col items-center justify-center ${scoreColor}`}>
                        <span className="text-3xl font-bold tracking-tight">{score}</span>
                        <span className="text-xs font-semibold -mt-1 opacity-80">/100</span>
                    </div>
                </div>
                <div className="flex-1">
                    <h4 className="font-bold text-white flex items-center gap-2">
                        Initial Smile Score
                        {afterScore && <span className="text-teal-400 font-bold">&rarr; {afterScore}</span>}
                    </h4>
                    <p className="text-xs text-zinc-300 mt-1">{summary}</p>
                </div>
                <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
                    <ChevronDownIcon className="w-5 h-5 text-zinc-400" />
                </motion.div>
            </button>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        layout
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="px-4 pb-4 border-t border-white/10 overflow-y-auto max-h-72"
                    >
                        <div className="mt-4 space-y-4">
                            {Object.entries(analysisSections).map(([sectionKey, sectionValue]) => (
                                sectionValue && typeof sectionValue === 'object' && (
                                    <div key={sectionKey}>
                                        <h5 className="font-semibold text-zinc-200 text-sm mb-2">{formatKey(sectionKey)}</h5>
                                        <div className="space-y-2 border-l-2 border-zinc-700 pl-3">
                                            {Object.entries(sectionValue).map(([subKey, subValue]: [string, any]) => (
                                                subValue && subValue.evaluation && (
                                                    <div key={subKey} className="text-xs text-zinc-400">
                                                        <p className="font-medium text-zinc-300">{formatKey(subKey)}</p>
                                                        <p>{subValue.evaluation}</p>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default SmileScoreDisplay;
